// Display logic for the anonymous athlete profile at /athlete/:uid/:profileUrl.
//
// Pure functions only — no React, no fetching. The page and its components
// stay thin enough to read, and every rule that has an edge case (name
// splitting, redirect safety, which stats make the strip) is testable without
// mounting anything.

import { sportSlugFor, groupedMeasurablesFor } from './catalogModel';
import { formatByKey, EM_DASH, hasKeyFormat } from './measurableDisplay';
import { contentTypeLabel } from '../constants/videoContentTypes';

/**
 * The athlete's name as two parts. The hero colours the surname differently
 * and breaks the line between the two on mobile, so this cannot be one string.
 *
 * Prefers the split fields the API sends. The fallback exists only for a
 * payload served by a backend older than this feature, and it splits on the
 * LAST space — which is wrong for a compound surname, hence the real fields.
 *
 * @param {object} athlete public profile payload
 * @returns {{first: string, last: string}}
 */
export function displayName(athlete) {
  const a = athlete || {};
  if (a.first_name || a.last_name) {
    return { first: a.first_name || '', last: a.last_name || '' };
  }
  const joined = (a.name || '').trim();
  if (!joined) return { first: '', last: '' };
  const cut = joined.lastIndexOf(' ');
  if (cut === -1) return { first: joined, last: '' };
  return { first: joined.slice(0, cut), last: joined.slice(cut + 1) };
}

/**
 * A `next` destination that is safe to hand to react-router's navigate().
 *
 * `next` arrives in a URL that anybody can compose and text to a coach, so it
 * is attacker-controlled. Only a single-slash-rooted in-app path is allowed:
 * "//evil.com" and "https://evil.com" are both navigable away from the origin,
 * and a backslash is normalised to a forward slash by some browsers, so
 * "/\evil.com" is protocol-relative too.
 *
 * Per the WHATWG URL spec, browsers strip every ASCII tab and newline
 * character out of a URL string during parsing, wherever they sit. That
 * means "/\n/evil.com" survives a naive check — raw[1] is "\n", not "/" or
 * "\\" — but once it reaches window.location.href the browser deletes the
 * "\n" and what actually navigates is "//evil.com", off-origin. So any C0
 * control character (tab, CR, LF included) anywhere in the input is rejected
 * outright rather than stripped: a legitimate `next` never contains one.
 *
 * @param {string|null|undefined} raw
 * @returns {string|null} a safe path, or null to fall back to /dashboard
 */
export function sanitizeNext(raw) {
  if (typeof raw !== 'string' || raw === '') return null;
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f]/.test(raw)) return null;
  if (raw[0] !== '/') return null;
  if (raw[1] === '/' || raw[1] === '\\') return null;
  return raw;
}

/**
 * The auth-gated bridge route. Public links only ever carry the 8-digit `uid`;
 * this path is where it gets exchanged for the internal users.id the coach
 * athlete-detail route needs.
 *
 * `uid` is a zero-padded string and leading zeros are significant, so it is
 * never coerced to a number.
 *
 * @param {string} uid
 * @returns {string}
 */
export function coachDetailPath(uid) {
  return `/athletes/uid/${uid}`;
}

/**
 * Where every coach CTA on the page points for an anonymous visitor.
 *
 * @param {string} uid
 * @returns {string}
 */
export function loginHref(uid) {
  const next = encodeURIComponent(coachDetailPath(uid));
  return `/login?next=${next}&from=public_profile`;
}

/**
 * Where a coach CTA points, for both visitor states.
 *
 * Five call sites across Hero, Rail and StickyCta had the same ternary inline.
 * They MUST agree — an anonymous visitor who is sent straight to the gated
 * detail route sees a role-gate bounce instead of the login form, and a
 * signed-in coach sent through /login takes a pointless round-trip — so the
 * rule lives once, here.
 *
 * @param {object} athlete public profile payload
 * @param {boolean} isCoach whether the viewer may open the coach detail page
 * @returns {string}
 */
export function coachCtaHref(athlete, isCoach) {
  const uid = (athlete || {}).uid;
  return isCoach ? coachDetailPath(uid) : loginHref(uid);
}

/**
 * The printable player-card route for this athlete.
 *
 * Two-segment and easy to get subtly wrong (uid before profile_url, `/card`
 * last); it was duplicated in the hero and the rail, where a drift would send
 * one of the two buttons to a 404.
 *
 * @param {object} athlete public profile payload
 * @returns {string}
 */
export function playerCardPath(athlete) {
  const a = athlete || {};
  return `/athlete/${a.uid}/${a.profile_url}/card`;
}

// Short overlay tags for the video cards. The design draws exactly two
// treatments — "Game Film" in signal-gold and "Training" in green-soft — while
// videoContentTypes.js defines five content types, so the remaining three take
// a neutral tag rather than being force-fitted into a colour that means
// something else. Modifiers are named for what the tag IS, not what colour it
// happens to be, so a future restyle cannot make the class names lie.
const VIDEO_TAGS = {
  game_competition: { label: 'Game Film', modifier: 'game' },
  skills_reel: { label: 'Training', modifier: 'training' },
  showcase_event: { label: 'Showcase', modifier: 'neutral' },
  highlight: { label: 'Highlight', modifier: 'neutral' },
  full_routine: { label: 'Routine', modifier: 'neutral' },
};

/**
 * The content-type tag overlaid on a video card, or null when the clip has no
 * content type at all.
 *
 * Null rather than a default bucket on purpose: the tag is a factual claim
 * about what the film shows, on a recruiting profile. Guessing "Highlight" for
 * a clip whose type is genuinely unknown is worse than showing no tag.
 *
 * @param {object} video a public-payload video
 * @returns {{label: string, modifier: string}|null}
 */
export function videoTag(video) {
  return VIDEO_TAGS[(video || {}).content_type] || null;
}

/**
 * A video card's title.
 *
 * The athlete's own label wins. Failing that, the content type's display name
 * — and failing THAT, the legacy storage category, which is what the page
 * showed before the redesign. Skipping the last step would caption every
 * type-less clip "Highlight Reel", contentTypeLabel's default bucket, which
 * publicly mislabels a combine or verification clip.
 *
 * @param {object} video a public-payload video
 * @returns {string}
 */
export function videoTitle(video) {
  const v = video || {};
  if (v.label) return v.label;
  if (v.content_type) return contentTypeLabel(v.content_type);
  return String(v.category || '').replace(/_/g, ' ');
}

/**
 * The sticky mobile bar's identity line: "Jaylen Carter · WR/CB '27".
 * Positions and class year are both optional. They share a single trailing
 * segment (joined by a plain space, "WR/CB '27") so that dropping either one
 * never leaves a bare " · " with nothing after it.
 *
 * @param {object} athlete
 * @returns {string}
 */
export function stickyCtaLine(athlete) {
  const a = athlete || {};
  const { first, last } = displayName(a);
  const name = [first, last].filter(Boolean).join(' ');
  const positions = [a.position_primary, a.position_secondary, a.position_other]
    .filter(Boolean).join('/');
  const year = a.graduation_year ? `'${String(a.graduation_year).slice(-2)}` : '';
  const trailer = [positions, year].filter(Boolean).join(' ');
  return [name, trailer].filter(Boolean).join(' · ');
}

// The hero strip is a fixed repeat(3, 1fr) grid at mobile widths, so a
// variable cell count leaves a ragged final row. Desktop's auto-fit does not
// care, so pinning the count at six lets one selection rule serve both.
export const STAT_STRIP_CELLS = 6;

// The Academic block is not in the sport catalog — GPA, test scores and honors
// are flat profile columns shared by every sport — so it is defined here and
// appended after whatever the catalog produced.
const ACADEMIC_FIELDS = [
  { key: 'gpa_weighted', label: 'GPA (W)' },
  { key: 'gpa_unweighted', label: 'GPA (UW)' },
  { key: 'act_score', label: 'ACT' },
  { key: 'sat_score', label: 'SAT' },
  { key: 'honors', label: 'Honors' },
];

// Catalog units are storage units; these are how a coach reads them. Lengths
// that need feet-and-inches or eighths are handled by formatByKey instead.
const UNIT_SUFFIX = { lb: ' lbs', s: 's', in: '"' };

/**
 * A measurable's value and verification, from the KV array if present and the
 * legacy flat column otherwise.
 *
 * The KV store is authoritative, but a profile whose rows predate the KV
 * migration only has the flat column. For every catalog measurable the KV key
 * and the legacy column name are the same string, which is what makes the
 * fallback a direct lookup.
 *
 * @param {object} athlete public profile payload
 * @param {string} key measurable key
 * @returns {{value: *, verified: boolean}}
 */
export function measurableValue(athlete, key) {
  const a = athlete || {};
  const row = (a.measurables || []).find(m => m.key === key);
  if (row && row.value !== null && row.value !== undefined && row.value !== '') {
    return { value: row.value, verified: !!row.verified };
  }
  const flat = a[key];
  if (flat !== null && flat !== undefined && flat !== '') {
    return { value: flat, verified: !!a[`${key}_verified`] };
  }
  return { value: null, verified: false };
}

/**
 * A measurable as a coach reads it. Delegates the two special length formats
 * to measurableDisplay so the profile, the report HTML and the report PDF
 * cannot disagree, and suffixes the rest from the catalog unit.
 *
 * @param {string} key measurable key
 * @param {*} value stored value
 * @param {string|null} unit catalog unit
 * @returns {string}
 */
export function formatMeasurable(key, value, unit) {
  if (value === null || value === undefined || value === '') return EM_DASH;
  if (hasKeyFormat(key)) {
    return formatByKey(key, value);
  }
  return `${value}${UNIT_SUFFIX[unit] || ''}`;
}

function catalogMeasurables(athlete, catalog) {
  const slug = sportSlugFor(catalog, (athlete || {}).sport);
  return groupedMeasurablesFor(catalog, slug);
}

/**
 * The six headline stats across the bottom of the hero.
 *
 * Order is height, weight, then the sport's own measurables in catalog order,
 * then GPA. Always exactly six cells: truncated if the athlete has more,
 * padded with empty cells if fewer, so the fixed mobile grid stays square.
 *
 * @param {object} athlete public profile payload
 * @param {object} catalog /api/sports/catalog payload
 * @returns {Array<{key: string, label: string, value: string|null, verified: boolean}>}
 */
export function pickStatStrip(athlete, catalog) {
  const groups = catalogMeasurables(athlete, catalog);
  const all = groups.flatMap(g => g.measurables);

  const lead = ['height_inches', 'weight_lbs'];
  const ordered = [
    ...lead.map(k => all.find(m => m.key === k)).filter(Boolean),
    ...all.filter(m => !lead.includes(m.key)),
  ];

  const cells = [];
  const pushCell = (key, label, unit) => {
    const { value, verified } = measurableValue(athlete, key);
    if (value === null) return false;
    cells.push({ key, label, value: formatMeasurable(key, value, unit), verified });
    return true;
  };

  ordered.forEach(m => {
    if (cells.length < STAT_STRIP_CELLS) pushCell(m.key, m.label, m.unit);
  });

  if (cells.length < STAT_STRIP_CELLS) {
    const gpaKey = (athlete || {}).gpa_weighted != null ? 'gpa_weighted' : 'gpa_unweighted';
    const gpaLabel = gpaKey === 'gpa_weighted' ? 'GPA (W)' : 'GPA (UW)';
    pushCell(gpaKey, gpaLabel, null);
  }

  // Pad with the next catalog entries the athlete has no value for, so the
  // strip reads as "not measured yet" rather than as a broken grid.
  const usedKeys = new Set(cells.map(c => c.key));
  ordered.forEach(m => {
    if (cells.length < STAT_STRIP_CELLS && !usedKeys.has(m.key)) {
      cells.push({ key: m.key, label: m.label, value: null, verified: false });
      usedKeys.add(m.key);
    }
  });
  while (cells.length < STAT_STRIP_CELLS) {
    cells.push({ key: `pad-${cells.length}`, label: '', value: null, verified: false });
  }

  return cells.slice(0, STAT_STRIP_CELLS);
}

/**
 * The Verified Measurables card grid: one group per catalog category that has
 * at least one value, plus Academic when there is anything academic on file.
 *
 * Rows are only the measurables the athlete actually has. The hint counts
 * those against the group's catalog total, which is the honest reading of
 * "2 OF 4" — two of the four things this sport measures here.
 *
 * @param {object} athlete public profile payload
 * @param {object} catalog /api/sports/catalog payload
 * @returns {Array<{title: string, hint: string, rows: Array<{key, label, value, verified}>}>}
 */
export function buildMeasurableGroups(athlete, catalog) {
  const out = [];

  catalogMeasurables(athlete, catalog).forEach(group => {
    const rows = [];
    group.measurables.forEach(m => {
      const { value, verified } = measurableValue(athlete, m.key);
      if (value === null) return;
      rows.push({
        key: m.key,
        label: m.label,
        value: formatMeasurable(m.key, value, m.unit),
        verified,
      });
    });
    if (rows.length === 0) return;
    out.push({
      title: group.category || 'Measurables',
      hint: `${rows.length} OF ${group.measurables.length}`,
      rows,
    });
  });

  const academic = [];
  ACADEMIC_FIELDS.forEach(f => {
    const { value, verified } = measurableValue(athlete, f.key);
    if (value === null) return;
    // honors is the one Academic field stored as a boolean with a `false`
    // default (backend/app/models/models.py), so "present" and "true" differ
    // only for it: measurableValue correctly reports false as present (it's
    // not null/undefined/''), but a public profile must not render "Honors:
    // No" for the common case of an athlete with none. Skip the row outright
    // rather than special-casing the string, which also keeps the "N OF 5"
    // hint honest instead of counting a false as a value earned.
    if (f.key === 'honors' && !value) return;
    academic.push({
      key: f.key,
      label: f.label,
      value: f.key === 'honors' ? 'Yes' : String(value),
      verified,
    });
  });
  if (academic.length > 0) {
    out.push({
      title: 'Academic',
      hint: `${academic.length} OF ${ACADEMIC_FIELDS.length}`,
      rows: academic,
    });
  }

  return out;
}

// Handle -> profile URL, per platform. Some platforms are stored as either a
// bare handle or a pasted full URL; those pass a full URL straight through.
const SOCIAL_PLATFORMS = [
  { platform: 'youtube', field: 'youtube_handle', allowUrl: true,
    build: h => `https://youtube.com/@${h}` },
  { platform: 'tiktok', field: 'tiktok_handle', allowUrl: false,
    build: h => `https://tiktok.com/@${h}` },
  { platform: 'instagram', field: 'instagram_handle', allowUrl: false,
    build: h => `https://instagram.com/${h}` },
  { platform: 'x', field: 'twitter_handle', allowUrl: false,
    build: h => `https://x.com/${h}` },
  { platform: 'snapchat', field: 'snapchat_handle', allowUrl: false,
    build: h => `https://snapchat.com/add/${h}` },
  { platform: 'facebook', field: 'facebook_handle', allowUrl: true,
    build: h => `https://facebook.com/${h}` },
  { platform: 'linkedin', field: 'linkedin_handle', allowUrl: true,
    build: h => `https://linkedin.com/in/${h}` },
];

/**
 * The rail's social pills. Same URL rules the page used before the redesign.
 *
 * @param {object} athlete public profile payload
 * @returns {Array<{platform: string, label: string, href: string}>}
 */
export function socialLinks(athlete) {
  const a = athlete || {};
  return SOCIAL_PLATFORMS.reduce((acc, p) => {
    const raw = a[p.field];
    if (!raw) return acc;
    const handle = String(raw).replace('@', '');
    const href = p.allowUrl && String(raw).startsWith('http') ? String(raw) : p.build(handle);
    acc.push({ platform: p.platform, label: handle, href });
    return acc;
  }, []);
}
